import React from 'react';
import { Share2, Copy, Twitter, Facebook, Mail } from 'lucide-react';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from '@/components/ui/button';
import { Event } from '@/types/api'; // Assuming this type definition exists

interface ShareButtonProps {
    event: Event;
    showExpandedCTA: boolean;
}

// Helper function to truncate text (similar to the example)
function truncateText(text: string, maxLength: number = 30): string {
    if (text.length > maxLength) {
        return text.substring(0, maxLength) + "...";
    }
    return text;
}

const ShareButton = ({ event, showExpandedCTA }: ShareButtonProps) => {
    // Use event.id if available, otherwise fallback to the current page's URL.
    const eventUrl = `https://allmygigs.com/event/${event.id}` || (typeof window !== 'undefined' ? window.location.href : '');
    const eventTitle = event.title;
    const eventDescription = event.description || '';

    const createTwitterShareUrl = (): string => {
        const text = `Check out this event: ${eventTitle}`;
        return `https://twitter.com/intent/tweet?url=${encodeURIComponent(eventUrl)}&text=${encodeURIComponent(text)}`;
    };

    const createFacebookShareUrl = (): string => {
        return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(eventUrl)}`;
    };

    const createEmailShareUrl = (): string => {
        const subject = `Check out this event: ${eventTitle}`;
        const body = `Hi,\n\nI thought you might be interested in this event:\n\nTitle: ${eventTitle}\n${eventDescription ? `Description: ${eventDescription}\n` : ''}Link: ${eventUrl}\n\nRegards,`;
        return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body.replace(/\n/g, '\r\n'))}`;
    };

    const copyLinkToClipboard = async () => {
        if (!eventUrl) {
            alert('No event URL available to copy.');
            return;
        }
        try {
            await navigator.clipboard.writeText(eventUrl);
            alert('Link copied, ready to paste!'); // Consider using a more subtle toast notification
        } catch (err) {
            console.error('Failed to copy link: ', err);
            alert('Failed to copy link.');
        }
    };

    return (
        <AlertDialog>
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <AlertDialogTrigger asChild>
                            <div className="group relative">
                                <div className="absolute inset-0 rounded-full bg-sky-300 opacity-0 animate-pulse-glow group-hover:opacity-20 transition-opacity duration-300"></div>

                                <Button
                                    variant="ghost"
                                    className="relative rounded-full bg-violet-100 hover:bg-violet-200 transition-colors
                                                                              group-hover:rotate-6 duration-300 px-3 py-1.5 h-auto text-sm text-violet-600
                                                                              font-medium ring-2 ring-violet-200 ring-offset-1 ring-offset-white
                                                                              hover:ring-violet-300 focus:ring-violet-400"
                                >
                                    <Share2 className="h-4 w-4 text-violet-800 mr-1.5 inline-block" />
                                    {showExpandedCTA ? 'Share event' : 'Share'}

                                    <span className="absolute inset-0 rounded-full bg-gradient-to-r from-sky-200 to-transparent
                                                    opacity-0 group-hover:opacity-30 blur-sm transition-opacity duration-300"></span>
                                </Button>
                            </div>
                        </AlertDialogTrigger>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>Share "{truncateText(event.title, 20)}"</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <AlertDialogContent className="w-full max-w-sm">
                <AlertDialogHeader>
                    <AlertDialogTitle>Share "{truncateText(event.title)}"</AlertDialogTitle>
                    <AlertDialogDescription>
                        Choose how you'd like to share this event.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
                    {eventUrl && ( // Only show "Copy Link" if eventUrl is available
                        <AlertDialogAction
                            className="w-full flex items-center justify-center"
                            onClick={copyLinkToClipboard}
                        >
                            <Copy className="h-4 w-4 mr-2" />
                            Copy Link
                        </AlertDialogAction>
                    )}
                    <AlertDialogAction
                        className="w-full flex items-center justify-center"
                        onClick={() => window.open(createTwitterShareUrl(), '_blank', 'noopener,noreferrer')}
                    >
                        <Twitter className="h-4 w-4 mr-2" />
                        Share on Twitter
                    </AlertDialogAction>
                    <AlertDialogAction
                        className="w-full flex items-center justify-center"
                        onClick={() => window.open(createFacebookShareUrl(), '_blank', 'noopener,noreferrer')}
                    >
                        <Facebook className="h-4 w-4 mr-2" />
                        Share on Facebook
                    </AlertDialogAction>
                    <AlertDialogAction
                        className="w-full flex items-center justify-center"
                        onClick={() => window.location.href = createEmailShareUrl()}
                    >
                        <Mail className="h-4 w-4 mr-2" />
                        Share via Email
                    </AlertDialogAction>
                    <AlertDialogCancel className="w-full mt-2">Cancel</AlertDialogCancel>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
};

export default ShareButton;